import { JobHubJob, createRecruiterObject } from "./jobUtils";
import { Job, RecruitmentStatus } from "./schema";

// Job form state interface
export interface JobFormState {
  title: string;
  employmentType: string;
  category: string;
  jobId: string;
  location: string;
  remoteType: string;
  salary: string;
  rewardInAirPoints: string;
  description: string;
  status: string;
  priority: string;
  recruitmentStatus: RecruitmentStatus;
  skills: string[];
  skillsInput: string;
  assignedRecruiter: string;
  importantNotes: string;
}

// Initial form state
export const getInitialJobFormState = (): JobFormState => ({
  title: "",
  employmentType: "full-time",
  category: "",
  jobId: "",
  location: "",
  remoteType: "onsite",
  salary: "",
  rewardInAirPoints: "",
  description: "",
  status: "draft",
  priority: "normal",
  recruitmentStatus: RecruitmentStatus.OPEN,
  skills: [],
  skillsInput: "",
  assignedRecruiter: "unassigned",
  importantNotes: ""
});

// Convert job to form state (works with both JobHubJob and Job types)
export const jobToFormState = (job: JobHubJob | Job): JobFormState => {
  // Always reset assigned recruiter to "unassigned" when editing
  // This forces users to explicitly select the correct recruiter
  let assignedRecruiterId = "unassigned";

  return {
    title: job.title,
    employmentType: job.employmentType,
    category: job.category,
    jobId: 'internalJobId' in job ? (job as any).internalJobId : (job as any).jobId,
    location: job.location,
    remoteType: 'remoteType' in job ? (job as any).remoteType : "onsite",
    salary: job.salary || "",
    rewardInAirPoints: job.rewardInAirPoints?.toString() || "",
    description: job.description,
    status: job.status,
    priority: job.priority,
    recruitmentStatus: 'recruitmentStatus' in job && (job as any).recruitmentStatus 
      ? (job as any).recruitmentStatus 
      : RecruitmentStatus.OPEN,
    skills: job.skills,
    skillsInput: "",
    assignedRecruiter: assignedRecruiterId,
    importantNotes: 'importantNotes' in job ? (job as any).importantNotes || "" : ""
  };
};

// Convert form state to job data
export const formStateToJobData = (formState: JobFormState, recruiters?: Array<{id: string, firstName: string, lastName: string}>) => {
  const data: any = {
    title: formState.title,
    employmentType: formState.employmentType,
    category: formState.category,
    internalJobId: formState.jobId,
    location: formState.location,
    remoteType: formState.remoteType,
    description: formState.description,
    status: formState.status,
    priority: formState.priority,
    recruitmentStatus: formState.recruitmentStatus,
    skills: formState.skills
  };

  // Add optional fields only if they have values
  if (formState.salary?.trim()) {
    data.salary = formState.salary.trim();
  }

  if (formState.rewardInAirPoints && parseInt(formState.rewardInAirPoints) > 0) {
    data.rewardInAirPoints = parseInt(formState.rewardInAirPoints);
  }

  // Handle recruiter assignment
  if (formState.assignedRecruiter && formState.assignedRecruiter !== "unassigned") {
    const recruiterObj = createRecruiterObject(formState.assignedRecruiter, recruiters);
    if (recruiterObj) {
      data.assignedRecruiter = recruiterObj;
    }
  }

  // Always include importantNotes (allows clearing the field when editing)
  data.importantNotes = formState.importantNotes?.trim() || "";

  return data;
};

// Form validation
// isNewJob: When true, importantNotes is required (only for new job creation, not editing)
export const validateJobForm = (formState: JobFormState, isNewJob: boolean = false): boolean => {
  const baseValidation = !!(
    formState.title &&
    formState.employmentType &&
    formState.category &&
    formState.jobId &&
    formState.location &&
    formState.remoteType &&
    formState.description
  );

  // Require importantNotes only for new jobs
  if (isNewJob) {
    return baseValidation && !!(formState.importantNotes?.trim());
  }

  return baseValidation;
};

// Handle skill input keyboard events
export const handleSkillKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
  currentInput: string,
  onAddSkill: (skill: string) => void,
  onClearInput: () => void
) => {
  if (event.key === 'Enter' || event.key === ',') {
    event.preventDefault();
    onAddSkill(currentInput);
    onClearInput();
  }
};

// Handle skill input blur
export const handleSkillBlur = (
  currentInput: string,
  onAddSkill: (skill: string) => void,
  onClearInput: () => void
) => {
  if (currentInput.trim()) {
    onAddSkill(currentInput);
    onClearInput();
  }
};

// Employment type options
export const EMPLOYMENT_TYPES = [
  { value: "full-time", label: "Full-time" },
  { value: "part-time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "freelance", label: "Freelance" },
  { value: "internship", label: "Internship" }
];

// Remote type options
export const REMOTE_TYPE_OPTIONS = [
  { value: "onsite", label: "On-site" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" }
];

// Job category options
export const JOB_CATEGORIES = [
  { value: "engineering", label: "Engineering" },
  { value: "design", label: "Design" },
  { value: "product", label: "Product" },
  { value: "marketing", label: "Marketing" },
  { value: "sales", label: "Sales" },
  { value: "operations", label: "Operations" },
  { value: "finance", label: "Finance" },
  { value: "hr", label: "Human Resources" },
  { value: "other", label: "Other" }
];

// Job status options
export const JOB_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "closed", label: "Closed" },
  { value: "filled", label: "Filled" }
];

// Job priority options
export const JOB_PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" }
];

// Recruiter options
export const RECRUITER_OPTIONS = [
  { value: "unassigned", label: "Unassigned" },
  { value: "Sarah Johnson", label: "Sarah Johnson" },
  { value: "Mike Chen", label: "Mike Chen" },
  { value: "Lisa Martinez", label: "Lisa Martinez" },
  { value: "David Kim", label: "David Kim" },
  { value: "Emily Rodriguez", label: "Emily Rodriguez" }
];
